import React, { useState } from 'react';
import { Container, Typography, Paper, Box, CircularProgress, Button } from '@mui/material';
import { TrainingForm } from './TrainingForm';
import { TrainingPlanDisplay } from './TrainingPlanDisplay';
import type { TrainingFormData, TrainingPlan } from '../types';

const getApiEndpoint = () => {
  const isProd = process.env.NODE_ENV === 'production';
  const isProdDomain = typeof window !== 'undefined' && window.location.hostname === 'mruns.com';

  if (isProd || isProdDomain) {
    return 'https://silwal.com/api/generate-plan'; // Always use HTTPS for production
  }

  return process.env.REACT_APP_GENI_API_CALL || '/api/generate-plan';
};

const API_ENDPOINT = getApiEndpoint();

const generatePlan = async (data: TrainingFormData): Promise<TrainingPlan> => {
  const response = await fetch(API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to generate plan from backend.' }));
    throw new Error(errorData.message || 'An unknown error occurred.');
  }
  
  const parsedResponse = await response.json();

  // Check if the training plan is nested under a 'training_plan' key or is the top-level object
  const trainingPlan = parsedResponse.training_plan || parsedResponse;

  if (!trainingPlan || !trainingPlan.weekly_plan) {
    console.error("Received JSON:", parsedResponse);
    throw new Error("Invalid plan structure received from API.");
  }

  return trainingPlan as TrainingPlan;
};

const TrainingPlanView: React.FC = () => {
  const [trainingPlan, setTrainingPlan] = useState<TrainingPlan | null>(null);
  const [formData, setFormData] = useState<TrainingFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleFormSubmit = async (data: TrainingFormData) => {
    setIsLoading(true);
    setError(null);
    setFormData(data);
    setIsRetrying(false);
    try {
      const plan = await generatePlan(data);
      setTrainingPlan(plan);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred. Please try again.');
      }
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setTrainingPlan(null);
    setFormData(null);
    setError(null);
    setIsRetrying(false);
  };

  const handleTryAgain = () => {
    setError(null);
    setTrainingPlan(null);
    setIsRetrying(true);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Generating your personalized plan...</Typography>
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography color="error">{error}</Typography>
          <Button onClick={handleTryAgain} sx={{ mt: 2 }}>Try Again</Button>
        </Box>
      );
    }
    if (trainingPlan && formData) {
      return <TrainingPlanDisplay plan={trainingPlan} formData={formData} onReset={handleReset} />;
    }
    if (isRetrying || !formData) {
      return (
        <>
          <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            Create Your Training Plan
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Tell us your goal, and we'll generate a personalized plan to get you there.
          </Typography>
          <TrainingForm onSubmit={handleFormSubmit} initialData={formData} />
        </>
      );
    }
    return null;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
        AI Running Coach
      </Typography>
      <Typography variant="h5" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
        Your Personal Path to Peak Performance
      </Typography>
      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        {renderContent()}
      </Paper>
    </Container>
  );
};

export default TrainingPlanView;
